import { useEffect, useRef, useState } from "react";

interface PeopleResult {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName?: string;
  jobTitle?: string;
  department?: string;
}

/**
 * An email recipient editor: type a name to search the directory (Graph), or
 * type a raw email address and press Enter. Selected recipients render as chips.
 */
export default function RecipientEditor({
  recipients,
  onChange,
  placeholder = "Search people or type an email…"
}: {
  recipients: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState<PeopleResult[]>([]);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    if (text.trim().length < 2 || (text.includes("@") && text.includes("."))) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`/api/people-search?q=${encodeURIComponent(text.trim())}`)
        .then((r) => r.json())
        .then((j: { results: PeopleResult[] }) => { if (!cancelled) { setSuggestions(j.results ?? []); setOpen(true); } })
        .catch(() => { if (!cancelled) setSuggestions([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [text]);

  const add = (email: string) => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) return;
    if (recipients.includes(e)) { setText(""); return; }
    onChange([...recipients, e]);
    setText("");
    setSuggestions([]);
    setOpen(false);
  };

  const remove = (email: string) => onChange(recipients.filter((r) => r !== email));

  return (
    <div className="recipient-editor" style={{ position: "relative" }}>
      {recipients.length > 0 && (
        <div className="recipient-chips">
          {recipients.map((r) => (
            <span key={r} className="recipient-chip">
              {r}
              <button type="button" onClick={() => remove(r)} aria-label={`Remove ${r}`}>×</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        value={text}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); add(text); }
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => { blurTimer.current = window.setTimeout(() => setOpen(false), 150); }}
        style={{ width: "100%", padding: ".5rem" }}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          className="person-suggestions"
          onMouseDown={(e) => { e.preventDefault(); if (blurTimer.current) clearTimeout(blurTimer.current); }}
        >
          {suggestions.map((s) => {
            const email = (s.mail || s.userPrincipalName || "").toLowerCase();
            return (
              <button
                type="button"
                key={s.id}
                className="person-suggestion"
                disabled={!email || recipients.includes(email)}
                onClick={() => add(email)}
              >
                <span className="person-suggestion-text">
                  <span className="person-name">{s.displayName}</span>
                  <span className="person-meta">{email}{s.department ? ` · ${s.department}` : ""}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
